import { useRef, type JSX } from 'react';
import { Clock, FileText, Send, Shield } from 'lucide-react';
import { Card, Header, Footer, Section, Step } from '../components';
import { WozCaseManagerResumeCase, WozCaseManagerStartCase } from '../components/woz-case-manager';

export const Home = (): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const chatbotVideo = '/videos/woz-chatbot.mp4';
  const chatbotAvatar = '/images/chatbot-avatar-512.png';

  const handleVideoEnd = () => {
    const pauseDuration = Math.floor(Math.random() * (45000 - 15000 + 1)) + 15000;
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play();
      }
    }, pauseDuration);
  };

  return (
    <div className='page-wrapper'>
      <Header />
      <Section paddingY='large' maxWidth='wide'>
        <div className='hero-grid'>
          <div className='hero-avatar'>
            <video
              ref={videoRef}
              src={chatbotVideo}
              poster={chatbotAvatar}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              className='hero-video'
              onError={(e) => {
                const img = document.createElement('img');
                img.src = chatbotAvatar;
                img.className = 'hero-video';
                e.currentTarget.replaceWith(img);
              }}
            />
          </div>
          <div className='hero-content'>
            <h1 className='hero'>Maak eenvoudig bezwaar tegen je WOZ-waarde</h1>
            <p className='hero-text'>
              Ik help je om binnen een paar minuten een sterk bezwaar te maken — precies zoals een echte taxateur werkt.
              Dat maakt jouw bezwaar makkelijker te beoordelen en vergroot je kans van slagen.
            </p>
            <p className='hero-pilot'>
              De bezwaarhulp is nog in ontwikkeling.{' '}
              <a href='mailto:info@footsteps.nl?subject=Feedback WOZ Bezwaarhulp' className='link-primary'>
                Feedback is welkom
              </a>
            </p>
            <div className='btn-group'>
              <WozCaseManagerStartCase />
              <WozCaseManagerResumeCase />
            </div>
          </div>
        </div>
      </Section>
      <Section paddingY='medium'>
        <div className='features-grid'>
          <Card
            icon={Clock}
            title='Snel & Eenvoudig'
            content='Voltooi je bezwaar in slechts 5-10 minuten met onze stap-voor-stap begeleiding'
            hoverable
          />
          <Card
            icon={FileText}
            title='Compleet Dossier'
            content="Upload foto's en documenten, voeg referenties toe en maak je bezwaar waterdicht"
            hoverable
          />
          <Card
            icon={Send}
            title='Direct Versturen'
            content='Je bezwaar wordt automatisch gecontroleerd en kan direct naar de gemeente'
            hoverable
          />
          <Card
            icon={Shield}
            title='Veilig & Privacy'
            content='Je gegevens worden veilig verwerkt en alleen met toestemming gedeeld'
            hoverable
          />
        </div>
      </Section>
      <Section background='dark' paddingY='medium' maxWidth='wide'>
        <div className='steps-grid'>
          <h2 className='section-title'>Zo werkt het</h2>
          <Step number={1} title='Vul je adres in' description='Geef je postcode en huisnummer op, dan zoeken wij de WOZ-gegevens automatisch op.' />
          <Step
            number={2}
            title='Goed onderbouwd'
            description='Hoe meer relevante details je toevoegt, hoe sterker je bezwaar. Wij helpen je stap voor stap om niets over het hoofd te zien.'
          />
          <Step
            number={3}
            title='Voeg bewijs toe'
            description="Upload foto's of documenten die je bezwaar sterker maken, zoals foto's van achterstallig onderhoud of gebreken"
          />
          <Step
            number={4}
            title='Veilig en privé'
            description='Controleer je samenvatting en verstuur direct naar de gemeente'
          />
        </div>
      </Section>
      <Section paddingY='medium' maxWidth='medium'>
        <div className='privacy-grid'>
          <h3>Privacy & Gegevensbescherming</h3>
          <Card title='Wat doen we met je gegevens?' titleAlign='left' contentAlign='left'>
            <ul>
              <li>Je gegevens worden alleen gebruikt voor het samenstellen van je bezwaar</li>
              <li>We delen niets zonder jouw expliciete toestemming</li>
              <li>Je kunt je gegevens op elk moment laten verwijderen</li>
              <li>We loggen geen inhoudelijke informatie zonder toestemming</li>
            </ul>
          </Card>
          <Card title='Beveiliging' titleAlign='left' contentAlign='left'>
            <ul>
              <li>SSL-versleuteling voor alle communicatie</li>
              <li>Veilige opslag van uploads en documenten</li>
              <li>Regelmatige beveiligingsupdates</li>
              <li>Rate limiting en spam bescherming</li>
            </ul>
          </Card>
        </div>
      </Section>
      <Footer />
    </div>
  );
};
